// Gorgias에서 Syncly를 연동하려 시도하는 경우 redirect되는 페이지
import { Select } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { CenterFlex, Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Heading from '@/components/Typography/Heading';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import useSidebar from '@/hooks/domain/useSidebar';
import service from '@/services/index';
import { useToast } from '@/stores/pure/ToastStore';
import useUIStore from '@/stores/pure/UIStore';
import useUserStore from '@/stores/UserStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { modalStyle } from '@/styles/style';

export const GORGIAS_ACCOUNT_SEARCH_PARAM = 'gorgias_account';

const GorgiasRedirectPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const addToast = useToast();
  const color = useColor();

  useSidebar(false);

  const colorTheme = useUIStore((state) => state.colorMode);

  const { workspaces, fetchWorkspace, fetchWorkspaces } = useWorkspaceStore(
    (state) => ({
      workspaces: state.workspaces,
      fetchWorkspace: state.fetchWorkspace,
      fetchWorkspaces: state.fetchWorkspaces,
    }),
  );

  const { setUser, user } = useUserStore((state) => ({
    setUser: state.setUser,
    user: state.user,
  }));

  const gorgias_account = searchParams.get('account');

  const query = useMemo(
    () =>
      gorgias_account
        ? `?${GORGIAS_ACCOUNT_SEARCH_PARAM}=${gorgias_account}`
        : null,
    [gorgias_account],
  );

  useEffect(() => {
    if (!query) return navigate('/');

    if (user) {
      if (!user.hasWorkspace) {
        navigate(`/create-workspace${query}`);
        return;
      }

      if (user.hasWorkspace && workspaces.length === 0) {
        fetchWorkspaces();
        return;
      }
    } else {
      (async () => {
        const data = await service.auth.authCheck();
        if (data && data.workspaces?.length) {
          setUser({
            user_id: data.user_id,
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
            hasWorkspace: true,
            color: data.color,
          });
        } else {
          navigate(`/login${query}`, {
            replace: true,
          });
        }
      })();
    }
  }, [
    user,
    navigate,
    setUser,
    workspaces,
    fetchWorkspaces,
    fetchWorkspace,
    location.pathname,
    query,
  ]);

  const [selected, setSelected] = useState<string | null>(null);

  const onConfirm = () => {
    if (!selected) return addToast('Please select workspace', 'fail');
    navigate(`/workspace/${selected}/settings/integration${query}`);
  };

  const onClickNewWorkspace = () => {
    navigate(`/create-workspace${query}`);
  };

  if (workspaces.length > 0)
    return (
      <Style>
        <VFlex className="panel">
          <Flex>
            <img
              src={
                colorTheme
                  ? `/assets/logo-gorgias_dark.svg`
                  : `/assets/logo-gorgias.svg`
              }
              alt="gorgias"
              width="24px"
              height="24px"
            />
          </Flex>
          <Space h={16} />
          <Heading type="h2">Gorgias integration</Heading>
          <Space h={8} />
          <Typo type="body2" color={color.gray600}>
            Gorgias will be connected to the selected workspace.
          </Typo>
          <Space h={16} />
          <VFlex g={8}>
            <Select
              placeholder="Select workspace"
              value={selected}
              onChange={(alias) => setSelected(alias)}
              size="large"
            >
              {workspaces.map((space) => (
                <Select.Option key={space.alias}>{space.name}</Select.Option>
              ))}
            </Select>
          </VFlex>
          <Space h={24} />
          <Button sizeType="l" onClick={onConfirm}>
            Confirm
          </Button>
          <Space h={16} />
          <Button sizeType="l" variant="outlined" onClick={onClickNewWorkspace}>
            Create new workspace
          </Button>
        </VFlex>
      </Style>
    );

  return null;
};

const Style = styled(CenterFlex)`
  position: relative;
  .panel {
    position: relative;
    width: 400px;
    padding: 32px;
    top: 64px;
    ${modalStyle};
  }
  .workspace-item {
    background-color: ${(props) => props.theme.colors.gray100};
    padding: 12px 24px;
    border-radius: 8px;
    cursor: pointer;
    &:hover {
      background-color: ${(props) => props.theme.colors.hover};
    }
  }
`;

export default GorgiasRedirectPage;
